import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useValidation } from '@hooks/validations';
import { ContractorTypeLabel, contractorTypeList, meetingDetailsInitialValues } from '.';
import { Dropdown, SubmitButton, TextInput, RadioButtonChip } from '@components/common';
import { useNavigate } from 'react-router-dom';
import { AccordianDetailList } from '@components/endUserDetails/components/moreDetailsForm/accordianDetailList';
import { useDebounce } from '@hooks/useDebounce';
import { useDealerListRxDb } from '@services/hooks/enduser-details/rxdbHooks/useDealerListRxDb';
import { DealersList } from '@components/endUserDetails/components/moreDetailsForm';
import { keyDownEvent } from '@helper/utils';
import { useTabNavigationGuard } from '@hooks/useTabNavigationGuard';
import { ToastType, useTosterStore } from '@store/toster';
import { useAddNewSiteStore } from '@store/lms/addNewSiteStore';
import { useEndUserStore } from '@store/enduser';
import { usePhoneNumberValidateQuery } from '@services/hooks/enduser-details/usePhoneNumberValidateQuery';
import { Modal } from '@components/common/modal/modal';
import { ContractorDetailsModal } from '@components/lms/common/contractorDetailsModal/contractorDetailsModal';
import { IContractorDetailsResponse } from '@services/hooks/lms/addNewSite';
import { useContractorDetailsQuery } from '@services/hooks/lms/addNewSite/useContractorDetailsQuery';
import { useSSDropdownQuery } from '@services/hooks/lms/addNewSite/useSSDropdownQuery';
import { useComponentLevelAccessCheck } from '../../../../../authGuard/hooks/useComponentLevelAccessCheck';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { useSiteContext } from '@components/lms/addNewSite';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { useSiteAllDetailsStore } from '@store/siteAllDetailsStore';

export const MeetingDetails = (): ReactElement => {
	useTabNavigationGuard(1);
	const navigate = useNavigate();
	const userRoleType = useUserRoleType();

	const { setToaster } = useTosterStore();
	const { activeTabIndex, setActiveTabIndex, isConvertingUpcomingToActive } = useEndUserStore();
	const { setKeyBaseValue, setSubKeyValue, typeOfContracts, noOfSites, contractorPotentialPerMonth } =
		useAddNewSiteStore();
	const { LmsMeetingDetailsValidationSchema } = useValidation();
	const { siteData, isEditing } = useSiteContext();
	const { clearSiteDetails } = useSiteAllDetailsStore();

	useSetMainHeader(isEditing ? 'Site Details' : 'Add New Site');

	// Clear site details store when component unmounts for new sites
	useEffect(() => {
		return (): void => {
			if (!isEditing) {
				clearSiteDetails();
			}
		};
	}, [isEditing, clearSiteDetails]);

	const { mutate: fetchContractorDetails } = useContractorDetailsQuery();
	const { data: ssList, isLoading: ssLoading } = useSSDropdownQuery();
	const permissionSSDropdownCheck = useComponentLevelAccessCheck('bdese-ss-dropdown');

	const [dealerSearch, setDealerSearch] = useState('');
	const [selectedDealerAddress, setSelectedDealerAddress] = useState('');

	const [isContractorModalOpen, setIsContractorModalOpen] = useState(false);
	const [contractorDetailsResult, setContractorDetailsResult] = useState<IContractorDetailsResponse | null>(null);
	const [phoneValidationData, setPhoneValidationData] = useState<unknown>(null);

	const { result: dealerList } = useDealerListRxDb({
		searchParams: [
			{ key: 'dealername_c', value: dealerSearch },
			{ key: 'dealercode_c', value: dealerSearch },
			{ key: 'dealeraddress1_c', value: dealerSearch },
			{ key: 'dealeraddress2_c', value: dealerSearch },
			{ key: 'dealerpin_c', value: dealerSearch },
		],
		sortBy: 'dealername_c',
	});

	const formik = useFormik({
		initialValues: meetingDetailsInitialValues,
		validationSchema: LmsMeetingDetailsValidationSchema,
		onSubmit: () => {
			handleNext();
		},
	});

	const { refetch: refetchPhoneValidation } = usePhoneNumberValidateQuery(
		formik.values.contractorPhoneNumber,
		'contractorPhoneNumber'
	);

	const handleDealerChange = useCallback(
		(fieldName: string, selectedValue: string) => {
			formik.setFieldValue(fieldName, selectedValue);
			setSelectedDealerAddress('');
			const selectedDealer = dealerList?.find(
				(d) => `${d._data?.dealercode_c} -  ${d._data?.dealername_c}` === selectedValue
			);

			if (selectedDealer) {
				const { dealeraddress1_c, dealeraddress2_c, dealerpin_c } = selectedDealer._data;
				const addressParts = [dealeraddress1_c, dealeraddress2_c, dealerpin_c].filter(Boolean);
				setSelectedDealerAddress(addressParts.join(', '));
			}
		},
		[dealerList, formik]
	);

	const ssOptions = useMemo((): DealersList[] => {
		if (ssList?.length) {
			return ssList.map((ssItem, index) => ({
				id: index,
				value: `${ssItem.sfid} -  ${ssItem.name}`,
				name: ssItem.name,
			}));
		}

		return [];
	}, [ssList]);

	const dealerOptions = useMemo((): DealersList[] => {
		if (dealerList?.length) {
			return dealerList.map((d, index) => ({
				id: index,
				value: `${d._data?.dealercode_c} -  ${d._data?.dealername_c}`,
				name: d._data?.dealername_c,
			}));
		}

		return [];
	}, [dealerList]);

	// Contractor details editable only when not captured earlier (re-visit with blank = can add; once captured = not editable)
	const contractorDetailsPreviouslyCaptured = useMemo(
		() => !!(siteData?.supervisor_name?.trim() && siteData?.supervisor_phone_number?.trim()),
		[siteData?.supervisor_name, siteData?.supervisor_phone_number]
	);

	const isContractorFieldsDisabled = isEditing && contractorDetailsPreviouslyCaptured;

	const onDropDownSearch = useDebounce((e: React.KeyboardEvent<HTMLInputElement>): void => {
		const element = e.target as HTMLInputElement;
		setDealerSearch(element.value);
	}, 300);

	const handlePrimaryNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		let val = e.target.value.replace(/\D/g, '');
		val = val.slice(0, 10);
		formik.setFieldValue('contractorPhoneNumber', val);
	};

	const handleContractorModalOpen = async (): Promise<void> => {
		if (!formik.values.contractorPhoneNumber || formik.values.contractorPhoneNumber.length !== 10) {
			setToaster(true, ToastType.error, 'Please enter a valid 10-digit phone number');
			return;
		}

		try {
			if (!refetchPhoneValidation) {
				setToaster(true, ToastType.error, 'Phone validation service unavailable');
				return;
			}

			const response = await refetchPhoneValidation();
			const validationData = response.data;
			setPhoneValidationData(validationData);

			if (validationData?.data?.exists) {
				fetchContractorDetails(
					{ phone: formik.values.contractorPhoneNumber },
					{
						onSuccess: (contractorDetails: IContractorDetailsResponse) => {
							if (!contractorDetails) {
								return;
							}

							setContractorDetailsResult(contractorDetails);
							setKeyBaseValue('contractorName', contractorDetails.name ?? '');
							setKeyBaseValue('membershipNo', contractorDetails.membershipno ?? '');
							formik.setFieldValue('contractorName', contractorDetails.name ?? '');

							if (contractorDetails.status === 'lead') {
								setKeyBaseValue('appIdC', contractorDetails.app_id_c ?? '');
							}
							if (contractorDetails.status === 'account') {
								setKeyBaseValue('endUserC', contractorDetails.sfid ?? '');
							}
						},
						onError: (error) => {
							setToaster(true, ToastType.error, error?.message ?? 'Failed to fetch contractor details');
						},
					}
				);
			}

			// Open the modal
			setIsContractorModalOpen(true);
		} catch (error) {
			setToaster(true, ToastType.error, 'Failed to validate phone number');
		}
	};

	const handleNextClick = (): void => {
		const store = useAddNewSiteStore.getState();

		if (
			formik.values.contractorType === ContractorTypeLabel.Contractor &&
			(!store.typeOfContracts || !store.noOfSites || !store.contractorPotentialPerMonth)
		) {
			setToaster(true, ToastType.error, 'Contractor details are required');
			return;
		}
		formik.handleSubmit();
	};

	const handleNext = useCallback(() => {
		const keys = [
			'contractorType',
			'contractorName',
			'contractorPhoneNumber',
			'homeOwnerDetails',
			'architectDetails',
			'dealerName',
			'assigneCmdi',
			'builderDetails',
		] as const;

		keys.forEach((key) => {
			setSubKeyValue('meetingDetails', key, formik.values[key]);
		});

		setActiveTabIndex(activeTabIndex + 1);
		navigate('/lms/add-new-site/glue-details');
	}, [activeTabIndex, formik.values, navigate, setActiveTabIndex, setSubKeyValue]);

	useEffect(() => {
		if (userRoleType) {
			formik.setFieldValue('role', userRoleType);
		}
	}, [userRoleType]);

	const hasSubmittedContractorDetails = useMemo((): boolean => {
		return Boolean(typeOfContracts && noOfSites && contractorPotentialPerMonth);
	}, [typeOfContracts, noOfSites, contractorPotentialPerMonth]);

	useEffect(() => {
		const store = useAddNewSiteStore.getState();

		if (!formik.values.contractorType && !formik.values.contractorName) {
			const initialValues = {
				contractorType: store.meetingDetails.contractorType,
				contractorName: store.meetingDetails.contractorName,
				contractorPhoneNumber: store.meetingDetails.contractorPhoneNumber,
				homeOwnerDetails: store.meetingDetails.homeOwnerDetails,
				architectDetails: store.meetingDetails.architectDetails,
				dealerName: store.meetingDetails.dealerName,
				assigneCmdi: store.meetingDetails.assigneCmdi,
				builderDetails: store.meetingDetails.builderDetails,
			};
			formik.setValues(initialValues);
		}

		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if ((isEditing || isConvertingUpcomingToActive) && siteData) {
			formik.setValues({
				contractorType: siteData.whom_did_you_meet_c,
				contractorName: siteData.supervisor_name,
				contractorPhoneNumber: siteData.supervisor_phone_number,
				homeOwnerDetails: [
					{
						id: 0,
						isActive: true,
						name: siteData.home_owner_name_c,
						number: siteData.home_owner_mobile_number_c,
					},
				],
				architectDetails: [
					{
						id: 0,
						isActive: true,
						name: siteData.architect_name_c ?? '',
						number: siteData.architect_mobile_no_c ?? '',
					},
				],
				builderDetails: [
					{
						id: 0,
						isActive: true,
						name: siteData.builder_name ?? '',
						number: siteData.builder_phone_number ?? '',
						address: siteData.builder_office_address ?? '',
					},
				],
				dealerName: siteData.dealer_name_c ?? '',
				assigneCmdi: siteData.assigned_cmdi_c,
			});
		}

		// eslint-disable-next-line
	}, [siteData, isEditing]);

	const handleContractorTypeChange = useCallback(
		(val: string) => {
			formik.setFieldValue('contractorType', val);

			if (val === ContractorTypeLabel.NONE) {
				formik.setFieldValue('contractorName', '');
				formik.setFieldValue('contractorPhoneNumber', '');
			} else if (val === ContractorTypeLabel.Contractor) {
				formik.setFieldValue('contractorName', '');
				formik.setFieldValue('contractorPhoneNumber', '');
			} else if (val === ContractorTypeLabel.Project_Supervisor) {
				formik.setFieldValue('contractorName', siteData?.supervisor_name || '');
				formik.setFieldValue('contractorPhoneNumber', siteData?.supervisor_phone_number || '');
			}
		},
		[formik, siteData]
	);

	const showContractorNameAndNumber =
		formik.values.contractorType && formik.values.contractorType !== ContractorTypeLabel.NONE;
	return (
		<div className="add-new-site-form">
			<form onSubmit={formik.handleSubmit}>
				<ul className="form-wrapper row">
					<li className="d-col">
						<h3 className="col-heading">Site Stakeholder Details</h3>
					</li>
					<li className="d-col">
						<RadioButtonChip
							label="Who did you meet at site?"
							options={contractorTypeList}
							value={formik.values.contractorType ?? ''}
							onChange={handleContractorTypeChange}
							onBlur={() => formik.setFieldTouched('contractorType', true)}
							error={
								formik.touched.contractorType && formik.errors.contractorType ? formik.errors.contractorType : null
							}
							required
							disabled={isEditing}
						/>
					</li>
					{showContractorNameAndNumber && (
						<>
							<li className="form-control d-col d-col-2">
								<TextInput
									label="Name"
									type="text"
									id="contractorName"
									name="contractorName"
									value={formik.values.contractorName ?? ''}
									setFieldValue={formik.setFieldValue}
									onBlur={formik.handleBlur}
									error={
										formik.touched.contractorName && formik.errors.contractorName ? formik.errors.contractorName : null
									}
									required
									disabled={isContractorFieldsDisabled}
								/>
							</li>
							<li className="form-control d-col d-col-2">
								<TextInput
									label="Phone Number"
									type="tel"
									id="contractorPhoneNumber"
									name="contractorPhoneNumber"
									onBlur={formik.handleBlur}
									value={formik.values.contractorPhoneNumber ?? ''}
									setFieldValue={formik.setFieldValue}
									error={
										formik.touched.contractorPhoneNumber && formik.errors.contractorPhoneNumber
											? formik.errors.contractorPhoneNumber
											: null
									}
									onKeyDown={keyDownEvent}
									maxLength={10}
									onChange={handlePrimaryNumberChange}
									required
									disabled={isContractorFieldsDisabled}
								/>
							</li>
						</>
					)}

					{formik.values.contractorType === ContractorTypeLabel.Contractor &&
						(!isEditing || !contractorDetailsPreviouslyCaptured) && (
							<li className="d-col contractor-submit-wrapper">
								<div className="contractor-status">
									{hasSubmittedContractorDetails ? (
										<>
											<span className="icon icon-alert-triangle"></span>
											<h5 className="text">Contractor details submitted.</h5>
										</>
									) : (
										<>
											<span className="icon icon-info"></span>
											<h5 className="text">Click Submit to check contractor enrollment status.</h5>
										</>
									)}
								</div>
								<button
									type="button"
									aria-label="submit contractor"
									className="rounded-btn primary-outline"
									disabled={
										!formik.values.contractorPhoneNumber ||
										formik.values.contractorPhoneNumber.length !== 10 ||
										!formik.values.contractorName ||
										Boolean(formik.errors.contractorName) ||
										Boolean(formik.errors.contractorPhoneNumber)
									}
									onClick={handleContractorModalOpen}
								>
									Submit
								</button>
							</li>
						)}

					<li className="d-col">
						<h3 className="col-heading">Other Stakeholder Details</h3>
					</li>
					{formik.values.homeOwnerDetails.map((owner, index) => (
						<AccordianDetailList
							key={owner.id}
							title="Home Owner"
							accordianName="homeOwnerDetails"
							accordianListIndex={index}
							formik={formik}
							data={owner}
							isEditing={isEditing}
						/>
					))}

					{formik.values.architectDetails.map((owner, index) => (
						<AccordianDetailList
							key={owner.id}
							title="Architect"
							accordianName="architectDetails"
							accordianListIndex={index}
							formik={formik}
							data={owner}
							isEditing={isEditing}
						/>
					))}
					{formik.values.builderDetails.map((builder, index) => (
						<AccordianDetailList
							key={builder.id}
							title="Builder"
							accordianName="builderDetails"
							accordianListIndex={index}
							formik={formik}
							data={builder}
							isEditing={isEditing}
							isBuilderDetail={true}
						/>
					))}
					<li className="d-col d-col-2">
						<Dropdown
							label="Dealer Name"
							id="dealerName"
							name="dealerName"
							onBlur={formik.handleBlur}
							title={formik.values.dealerName ?? ''}
							error={formik.touched.dealerName && formik.errors.dealerName ? formik.errors.dealerName : ''}
							options={dealerOptions}
							handelKeyDownProp={onDropDownSearch}
							listFetching={true}
							setFieldValue={handleDealerChange}
							disabled={isEditing}
						/>
					</li>
					<li className="d-col d-col-2">
						<div className="dealer-address-wrapper">
							<label className="dealer-address-title" htmlFor="dealerAddress">
								Dealer Address
							</label>
							<textarea
								className="dealer-address-textarea"
								id="dealerAddress"
								name="dealerAddress"
								rows={3}
								required
								value={selectedDealerAddress}
								disabled
							></textarea>
						</div>
					</li>
					{permissionSSDropdownCheck && (
						<li className="d-col d-col-2">
							<Dropdown
								label="Select SS to whom this site will be assigned?"
								id="assigneCmdi"
								name="assigneCmdi"
								onBlur={formik.handleBlur}
								title={formik.values.assigneCmdi ?? ''}
								error={formik.touched.assigneCmdi && formik.errors.assigneCmdi ? formik.errors.assigneCmdi : ''}
								options={ssOptions}
								listFetching={ssLoading}
								setFieldValue={formik.setFieldValue}
								required
								disabled={isEditing}
							/>
						</li>
					)}
				</ul>
				<SubmitButton isDisabled={isEditing || !formik.isValid || !formik.dirty} handleSubmitClick={handleNextClick} />
			</form>

			<Modal
				modalTitle={`Contractor Details`}
				modalOpen={isContractorModalOpen}
				modalId="contractorDetailsModal"
				isEscAllow={false}
				className="dialog-lg dialog-bottom dialog-conform"
			>
				<ContractorDetailsModal
					isContractorExists={phoneValidationData?.data?.exists ?? false}
					name={formik.values.contractorName ?? ''}
					phone={formik.values.contractorPhoneNumber ?? ''}
					contractorDetails={contractorDetailsResult || null}
					onCancel={() => setIsContractorModalOpen(false)}
				/>
			</Modal>
		</div>
	);
};
